/**
 * Weathered wooden fingerpost (`hush.signpost.v1`) — a ground piece marking a path near the hideout.
 *
 * Frame 80×130, support at the bottom-center (40, 130). A square post leans slightly (`lean` −1..1, positive =
 * right) and carries `boards` (2..3) arrow boards pointing different ways. The boards carry no text, only carved
 * grooves; a little moss sits on the cap and the boards, and a cloth rag is tied below them.
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies tilt and wear.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface SignpostParams {
  readonly boards: number; // 2..3 integer
  readonly lean: number;   // −1..1, positive leans right
}
export type SignpostRecipe = PieceRecipe<'hush.signpost.v1', SignpostParams>;

export const SIGNPOST_FRAME = { w: 80, h: 130 } as const;

export const HUSH_SIGNPOST: SignpostRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.signpost.fingerpost.v1', generatorId: 'hush.signpost.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { boards: 3, lean: -0.3 },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number, int = false): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi) || (int && !Number.isInteger(v))) {
    throw new Error(`hush.signpost.v1: ${k} must be ${int ? 'an integer ' : ''}${lo}..${hi}`);
  }
  return v;
}

const W = SIGNPOST_FRAME.w, H = SIGNPOST_FRAME.h;
const clamp = (p: Point): Point => ({ x: Math.min(W - 1.5, Math.max(1.5, p.x)), y: Math.min(H - 1, Math.max(1.5, p.y)) });

export function buildSignpost(recipe: SignpostRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.signpost.v1');
  checkScope(scope);
  const count = num(recipe.params, 'boards', 2, 3, true);
  const lean = num(recipe.params, 'lean', -1, 1);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // ---- post: a tapering square post from the fixed base, its top shifted by `lean` ----
  const topY = 16;
  const topX = W / 2 + lean * 6;
  const at = (y: number) => W / 2 + (topX - W / 2) * ((H - y) / (H - topY)); // post centerline x at height y
  const half = (y: number) => 3.4 + 1.2 * ((y - topY) / (H - topY));
  const postPts: Point[] = [
    { x: at(H) - half(H), y: H - 0.8 }, { x: at(topY) - half(topY), y: topY + 1.5 },
    { x: at(topY), y: topY - 1.5 }, { x: at(topY) + half(topY), y: topY + 1.5 }, { x: at(H) + half(H), y: H - 0.8 },
  ];
  let post = el('path', { d: poly(postPts), fill: roles.woodBase, stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' });
  post += el('path', { d: poly([{ x: at(topY + 4) - 1.4, y: topY + 4 }, { x: at(H - 4) - 2, y: H - 4 }], false),
    fill: 'none', stroke: roles.woodLight, 'stroke-width': 1.2, opacity: 0.8 });
  post += el('path', { d: poly([{ x: at(topY + 4) + 2, y: topY + 4 }, { x: at(H - 4) + 2.6, y: H - 4 }], false),
    fill: 'none', stroke: roles.woodShadow, 'stroke-width': 1.2, opacity: 0.7 });
  for (let i = 0; i < 4; i++) { // weathering cracks
    const y = topY + 20 + rnd(`crack.${i}.y`) * (H - topY - 30);
    const len = 4 + rnd(`crack.${i}.len`) * 7;
    const dx = (rnd(`crack.${i}.x`) - 0.5) * 2.4;
    post += el('path', { d: `M${n(at(y) + dx)} ${n(y)}L${n(at(y + len) + dx + 0.4)} ${n(y + len)}`,
      fill: 'none', stroke: roles.woodShadow, 'stroke-width': 0.6, opacity: 0.8 });
  }

  // ---- boards: arrow boards, alternating directions, each with a small seeded tilt ----
  let boards = '';
  let mossOnBoards = '';
  const firstDir = rnd('dir') < 0.5 ? -1 : 1;
  for (let i = 0; i < count; i++) {
    const dir = (i % 2 ? -firstDir : firstDir) * (i === 2 && rnd('third.flip') < 0.5 ? -1 : 1);
    const cy = topY + 8 + i * 17;
    const cx = at(cy);
    const len = 26 + rnd(`board.${i}.len`) * 8;
    const bh = 9 + rnd(`board.${i}.h`) * 2.5;
    const tilt = (rnd(`board.${i}.tilt`) - 0.5) * 0.24 + (dir > 0 ? 0.04 : -0.04);
    const tail = 7; // board passes behind the post a little
    const c = Math.cos(tilt), s = Math.sin(tilt);
    const tp = (u: number, v: number): Point => clamp({ x: cx + dir * (u * c) - v * s * dir, y: cy + u * s + v * c });
    const tip = len, shoulder = len - 6;
    const shape = [tp(-tail, -bh / 2), tp(shoulder, -bh / 2), tp(tip, 0), tp(shoulder, bh / 2), tp(-tail, bh / 2)];
    boards += el('path', { d: poly(shape), fill: i % 2 ? mix(roles.woodBase, roles.woodLight, 0.25) : roles.woodBase,
      stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
    boards += el('path', { d: poly([tp(-tail + 1, -bh / 2 + 1.2), tp(shoulder - 1, -bh / 2 + 1.2)], false),
      fill: 'none', stroke: roles.woodLight, 'stroke-width': 0.9, opacity: 0.8 });
    boards += el('path', { d: poly([tp(-tail + 1, bh / 2 - 1), tp(shoulder - 1, bh / 2 - 1)], false),
      fill: 'none', stroke: roles.woodShadow, 'stroke-width': 0.9, opacity: 0.7 });
    // carved grooves: two or three short notched lines, no lettering
    const grooves = 2 + Math.floor(rnd(`board.${i}.grooves`) * 2);
    for (let g = 0; g < grooves; g++) {
      const v = (g - (grooves - 1) / 2) * 2.3;
      const u0 = 4 + rnd(`board.${i}.g${g}.a`) * 4, u1 = shoulder - 3 - rnd(`board.${i}.g${g}.b`) * 6;
      boards += el('path', { d: poly([tp(u0, v), tp(u1, v)], false), fill: 'none', stroke: roles.woodShadow,
        'stroke-width': 0.8, 'stroke-linecap': 'round' });
    }
    // nail heads where the board meets the post
    boards += el('circle', { cx: tp(0, 0).x, cy: tp(0, 0).y, r: 1, fill: roles.stoneShadow, stroke: outline, 'stroke-width': 0.4 });
    if (rnd(`board.${i}.moss`) < 0.7) {
      const mu = 6 + rnd(`board.${i}.mossu`) * (shoulder - 12);
      const a = tp(mu, -bh / 2), b = tp(mu + 5 + rnd(`board.${i}.mossw`) * 5, -bh / 2);
      mossOnBoards += el('path', { d: `M${n(a.x)} ${n(a.y + 0.6)}Q${n((a.x + b.x) / 2)} ${n(Math.min(a.y, b.y) - 2.6)} ${n(b.x)} ${n(b.y + 0.6)}Z`,
        fill: roles.moss, stroke: roles.reedShadow, 'stroke-width': 0.5 });
    }
  }

  // ---- cap: a little pyramid cap with moss on it ----
  const capY = topY - 1.5;
  let cap = el('path', { d: poly([{ x: topX - 5.4, y: capY + 3.5 }, { x: topX, y: capY - 2.5 }, { x: topX + 5.4, y: capY + 3.5 }]),
    fill: roles.woodShadow, stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round' });
  cap += el('path', { d: `M${n(topX - 5)} ${n(capY + 3)}Q${n(topX - 1.5)} ${n(capY - 3.2)} ${n(topX + 2.5)} ${n(capY + 0.8)}Z`,
    fill: roles.moss, stroke: roles.reedShadow, 'stroke-width': 0.5 });

  // ---- rag: an undyed strip of cloth below the boards, two loose tails (blue cloth stays the Hush cue) ----
  const ragY = topY + 8 + count * 17 + 2;
  const rx = at(ragY), rh = half(ragY) + 0.8;
  let rag = el('path', { d: poly([{ x: rx - rh, y: ragY }, { x: rx + rh, y: ragY - 1 }, { x: rx + rh, y: ragY + 3 }, { x: rx - rh, y: ragY + 4 }]),
    fill: roles.paperBase, stroke: outline, 'stroke-width': 0.8, 'stroke-linejoin': 'round' });
  const side = rnd('rag.side') < 0.5 ? -1 : 1;
  for (let t = 0; t < 2; t++) {
    const x0 = rx + side * (rh - 0.5), y0 = ragY + 1 + t * 1.5;
    const tl = 9 + rnd(`rag.${t}.len`) * 6, sway = side * (3 + t * 3 + rnd(`rag.${t}.sway`) * 3);
    const end = clamp({ x: x0 + sway, y: y0 + tl });
    rag += el('path', { d: `M${n(x0)} ${n(y0)}Q${n(x0 + sway * 0.8)} ${n(y0 + tl * 0.35)} ${n(end.x)} ${n(end.y)}L${n(end.x - side * 2.4)} ${n(end.y - 1)}Q${n(x0 + sway * 0.3)} ${n(y0 + tl * 0.45)} ${n(x0)} ${n(y0 + 2.4)}Z`,
      fill: t ? roles.paperShadow : roles.paperLight, stroke: outline, 'stroke-width': 0.7, 'stroke-linejoin': 'round' });
  }

  // ---- ground: moss and grass at the foot so the post sits in the ground ----
  let ground = el('path', { d: `M${n(W / 2 - 13)} ${n(H - 0.5)}Q${n(W / 2)} ${n(H - 7)} ${n(W / 2 + 13)} ${n(H - 0.5)}Z`,
    fill: roles.moss, stroke: outline, 'stroke-width': 0.8 });
  for (let i = 0; i < 6; i++) {
    const x = W / 2 - 12 + i * 4.8 + (rnd(`tuft.${i}`) - 0.5) * 2;
    const h = 5 + rnd(`tuft.${i}.h`) * 6;
    ground += el('path', { d: `M${n(x - 1.5)} ${n(H - 0.6)}Q${n(x)} ${n(H - h * 0.6)} ${n(x + (i % 2 ? 2.2 : -2.2))} ${n(H - h)}Q${n(x + 0.6)} ${n(H - h * 0.4)} ${n(x + 1.5)} ${n(H - 0.6)}Z`,
      fill: i % 3 ? roles.reedBase : roles.reedShadow, stroke: outline, 'stroke-width': 0.5 });
  }

  const body = partGroup(parts, 'signpost-post', post)
    + partGroup(parts, 'signpost-boards', boards + mossOnBoards)
    + partGroup(parts, 'signpost-cap', cap)
    + partGroup(parts, 'signpost-rag', rag)
    + partGroup(parts, 'signpost-ground', ground);
  const support = { x: W / 2, y: H };
  return {
    frame: { ...SIGNPOST_FRAME },
    body,
    anchors: { support, post_top: { x: topX, y: capY - 2.5 } },
    parts,
  };
}
